import { Component, inject, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { forkJoin, of, throwError, firstValueFrom } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../../app.config';
import { FhirResourceTransformService } from '../../shared/fhir-resource-transform.service';
import { StateService } from '../../shared/state.service';
import { UtilityService } from '../../shared/utility.service';
import { AuthService } from '../../shared/auth/auth.service';
import { ResourceRefreshService } from '../../shared/resource-refresh.service';
import { ChargeItemDefinitionSearchService } from '../../shared/charge-item-definition-search.service';
import { SuccessMessageComponent } from '../../shared/success-message/success-message.component';
import { Bundle, Medication, ChargeItemDefinition } from 'fhir/r4';

type ImportAction = 'add' | 'edit' | 'delete';

@Component({
  selector: 'app-medication-import',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatRadioModule,
    FormsModule
  ],
  templateUrl: './medication-import.component.html',
  styleUrls: ['./medication-import.component.scss']
})
export class MedicationImportComponent {
  private http = inject(HttpClient);
  private baseApiUrl = inject(backendEndPointToken);
  private terminologyServer = inject(terminologyCanonicalUrlBaseToken);
  private fhirTransformService = inject(FhirResourceTransformService);
  private stateService = inject(StateService);
  private utilityService = inject(UtilityService);
  private auth = inject(AuthService);
  private dialogRef = inject(MatDialogRef<MedicationImportComponent>);
  private snackBar = inject(MatSnackBar);
  private resourceRefresh = inject(ResourceRefreshService);

  selectedFile: File | null = null;
  action: ImportAction = 'add';
  duplicateStrategy: 'skip' | 'add_new' = 'skip';
  private existingMedsByCode = new Map<string, any[]>();
  isImporting = false;
  progress = 0;
  importResults: { success: any[], failure: any[] } = { success: [], failure: [] };

  private readonly csvHeaders = [
    'id', 
    'name_text', 'name_code', 'name_display', 'name_system',
    'status', 
    'form_text', 'form_code', 'form_display', 'form_system',
    'manufacturer',
    'amountNumeratorValue', 'amountNumeratorUnit', 'amountDenominatorValue', 'amountDenominatorUnit',
    'ingredient1_name_text', 'ingredient1_name_code', 'ingredient1_name_display', 'ingredient1_name_system',
    'ingredient1_isActive', 'ingredient1_strengthNumeratorValue', 'ingredient1_strengthNumeratorUnit', 'ingredient1_strengthDenominatorValue', 'ingredient1_strengthDenominatorUnit',
    'lotNumber', 'expirationDate',
    'packageType', 'dispensableUnits', 'totalRemaining', 'lowStockThreshold', 'lowStockAlertsEnabled',
    'currency', 'sellingPrice', 'costPrice'
  ];

  async downloadTemplate() {
    try {
      const ExcelJS = await import('exceljs');
      const FileSaver = await import('file-saver');
      const Workbook = ExcelJS.Workbook || (ExcelJS as any).default?.Workbook || (ExcelJS as any).default;
      const workbook = new Workbook();
      
      let dictionary: any = null;
      try {
        dictionary = await firstValueFrom(this.http.get(`${this.baseApiUrl}/ValueSet/nafdac-medications`));
      } catch (e) {
        console.warn('Could not fetch nafdac-medications dictionary', e);
      }

      // Dictionary Sheet (Hidden)
      const dictSheet = workbook.addWorksheet('Dictionary', { state: 'hidden' });
      dictSheet.addRow(['Display Text', 'Code', 'System']);
      let dictRowCount = 1;
      
      const conceptList = dictionary?.compose?.include?.[0]?.concept || [];
      const systemUrl = dictionary?.compose?.include?.[0]?.system || '';
      for (const concept of conceptList) {
        if (concept.display && concept.code) {
          dictSheet.addRow([concept.display, concept.code, systemUrl]);
          dictRowCount++;
        }
      }

      // Instructions Sheet
      const instSheet = workbook.addWorksheet('Instructions');
      instSheet.getColumn(1).width = 40;
      instSheet.getColumn(2).width = 120;
      
      instSheet.addRow(['Elikita Medication Import Template', '']);
      instSheet.getRow(1).font = { bold: true, size: 14 };
      instSheet.addRow([]);
      
      instSheet.addRow(['General Instructions', '']);
      instSheet.getRow(4).font = { bold: true };
      instSheet.addRow(['1. "Medications" Sheet', 'All your data must go in the "Medications" sheet.']);
      instSheet.addRow(['2. Do not change headers', 'The exact column headers on row 1 of the "Medications" sheet must remain exactly as they are.']);
      instSheet.addRow(['3. Action: Add vs Edit', 'If you are adding new medications, leave the "id" column blank. If you are editing, the "id" column must contain the original FHIR ID.']);
      instSheet.addRow([]);
      
      instSheet.addRow(['Understanding Terminology Columns (Code, System, Display)', '']);
      instSheet.getRow(10).font = { bold: true };
      instSheet.addRow(['What are they?', 'FHIR represents concepts (like a medication name or form) using 4 fields: Text, Code, Display, and System.']);
      instSheet.addRow(['_text (e.g., name_text)', 'REQUIRED. This is the simple, human-readable name of the medicine (e.g., "Paracetamol 500mg").']);
      instSheet.addRow(['_code, _system, _display', 'OPTIONAL. These are for strict terminology bindings (e.g., SNOMED CT codes).']);
      instSheet.addRow(['If you are adding new data', 'Simply fill out the _text column (e.g., "name_text") and leave _code, _system, and _display completely blank. The system will handle it automatically.']);
      instSheet.addRow(['If you exported data to edit', 'The system will populate all 4 columns for you. If you just need to fix a typo, edit the _text column and leave the other columns exactly as they are.']);

      const dataSheet = workbook.addWorksheet('Medications');
      dataSheet.addRow(this.csvHeaders);
      const headerRow = dataSheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } };
      
      // Templates are NOT protected — users need full freedom to fill out blank rows.
      // No sheet.protect() call here (matching supply delivery template pattern).

      const expColIndex = this.csvHeaders.indexOf('expirationDate') + 1;
      const statusColIndex = this.csvHeaders.indexOf('status') + 1;
      const nameTextColIndex = this.csvHeaders.indexOf('name_text') + 1;
      const nameCodeColIndex = this.csvHeaders.indexOf('name_code') + 1;
      const nameDisplayColIndex = this.csvHeaders.indexOf('name_display') + 1;
      const nameSystemColIndex = this.csvHeaders.indexOf('name_system') + 1;
      const totalColumns = this.csvHeaders.length;
      
      // Pre-generate 50 empty rows with data validations and VLOOKUP formulas
      for(let i = 2; i <= 51; i++) {
        const row = dataSheet.addRow({});
        // Explicitly unlock every cell column-by-column (eachCell doesn't work on empty rows)
        for (let col = 1; col <= totalColumns; col++) {
          dataSheet.getCell(i, col).protection = { locked: false };
        }

        // Expiration Date validation
        const dateCell = dataSheet.getCell(i, expColIndex);
        dateCell.numFmt = 'yyyy-mm-dd';
        dateCell.dataValidation = {
          type: 'date',
          operator: 'greaterThan',
          showErrorMessage: true,
          allowBlank: true,
          formulae: [new Date('2000-01-01')],
          errorStyle: 'error',
          errorTitle: 'Invalid Date',
          error: 'Please enter a valid date (e.g. 2026-12-31)'
        };

        // Status validation
        const statusCell = dataSheet.getCell(i, statusColIndex);
        statusCell.dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: ['"active,inactive,entered-in-error"']
        };

        // Name Terminology validation & VLOOKUP formulas
        if (dictRowCount > 1) {
          const textCell = dataSheet.getCell(i, nameTextColIndex);
          textCell.dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [`'Dictionary'!$A$2:$A$${dictRowCount}`]
          };
          
          const textRef = textCell.address;
          const codeCell = dataSheet.getCell(i, nameCodeColIndex);
          const displayCell = dataSheet.getCell(i, nameDisplayColIndex);
          const systemCell = dataSheet.getCell(i, nameSystemColIndex);

          codeCell.value = { formula: `IFERROR(VLOOKUP(${textRef}, Dictionary!$A$2:$C$${dictRowCount}, 2, FALSE), "")` };
          displayCell.value = { formula: `IFERROR(VLOOKUP(${textRef}, Dictionary!$A$2:$C$${dictRowCount}, 1, FALSE), "")` };
          systemCell.value = { formula: `IFERROR(VLOOKUP(${textRef}, Dictionary!$A$2:$C$${dictRowCount}, 3, FALSE), "")` };
        }
      }
      
      // Samples Sheet
      const samplesSheet = workbook.addWorksheet('Samples');
      samplesSheet.addRow(this.csvHeaders);
      samplesSheet.getRow(1).font = { bold: true };
      
      const sample1 = [
        '', // id
        'Lonart (Artemether/Lumefantrine)', '', '', '', // name
        'active', // status
        'tablet', '', '', '', // form
        'Greenlife', // manufacturer
        '6', 'tablet', '1', 'pack', // amount
        'Artemether', '', '', '', // ingredient1 name
        'true', '20', 'mg', '1', 'tablet', // ingredient1 details
        'LOT-LN123', '2026-12-31', // batch
        'Pack', 'tablet', '50', '10', 'true', // inventory: packageType, dispensableUnits, totalRemaining, lowStockThreshold, lowStockAlertsEnabled
        'NGN', '2500', '1800' // price
      ];
      
      const sample2 = [
        '', // id
        'Emzor Paracetamol', '', '', '', // name
        'active', // status
        'tablet', '', '', '', // form
        'Emzor', // manufacturer
        '96', 'tablet', '1', 'pack', // amount
        'Paracetamol', '', '', '', // ingredient1 name
        'true', '500', 'mg', '1', 'tablet', // ingredient1 details
        'LOT-P456', '2027-05-31', // batch
        'Box', 'sachet', '200', '50', 'true', // inventory: packageType, dispensableUnits, totalRemaining, lowStockThreshold, lowStockAlertsEnabled
        'NGN', '1000', '600' // price
      ];
      samplesSheet.addRow(sample1);
      samplesSheet.addRow(sample2);
      
      // Auto-fit sample columns and HIDE technical columns
      const hideTechnicalColumns = (sheet: any) => {
        sheet.columns.forEach((column: any, index: number) => {
          const headerName = this.csvHeaders[index];
          if (headerName && (headerName.endsWith('_code') || headerName.endsWith('_system') || headerName.endsWith('_display'))) {
            column.hidden = true;
          } else {
            column.width = 15; // Set normal width for visible columns
          }
        });
      };
      
      hideTechnicalColumns(dataSheet);
      hideTechnicalColumns(samplesSheet);
      
      const buffer = await workbook.xlsx.writeBuffer();
      const saveAs = FileSaver.saveAs || (FileSaver as any).default?.saveAs || (FileSaver as any).default;
      saveAs(new Blob([buffer]), `medication_import_template_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (e) {
      console.error('Failed to generate Excel template', e);
      this.snackBar.open('Failed to generate Excel template. Please check console.', 'Close', { duration: 3000 });
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file && (file.name.endsWith('.csv') || file.name.endsWith('.xlsx'))) {
      this.selectedFile = file;
    } else {
      this.snackBar.open('Please select a valid CSV or XLSX file.', 'Close', { duration: 3000 });
      this.selectedFile = null;
    }
  }

  private chargeItemDefService = inject(ChargeItemDefinitionSearchService);
  private chargeItemMap = new Map<string, any>();

  async startImport() {
    if (!this.selectedFile) return;

    this.isImporting = true;
    this.progress = 0;
    this.importResults = { success: [], failure: [] };

    try {
      // Safely prefetch all ChargeItemDefinitions with pagination support, avoiding _count limits
      const priceMap = await this.chargeItemDefService.prefetch().toPromise();
      this.chargeItemMap.clear();
      
      if (priceMap) {
        priceMap.forEach((cid: any) => {
          if (cid?.instance?.[0]?.reference?.startsWith('Medication/')) {
            const medId = cid.instance[0].reference.replace('Medication/', '');
            this.chargeItemMap.set(medId, cid);
          }
        });
      }

      let rows: any[] = [];
      
      if (this.selectedFile.name.endsWith('.csv')) {
        const text = await this.selectedFile.text();
        rows = this.parseCSV(text);
      } else if (this.selectedFile.name.endsWith('.xlsx')) {
        const ExcelJS = await import('exceljs');
        const Workbook = ExcelJS.Workbook || (ExcelJS as any).default?.Workbook || (ExcelJS as any).default;
        const workbook = new Workbook();
        const arrayBuffer = await this.selectedFile.arrayBuffer();
        await workbook.xlsx.load(arrayBuffer);
        const worksheet = workbook.getWorksheet('Medications') || workbook.worksheets[0];
        
        const headers: string[] = [];
        worksheet.getRow(1).eachCell((cell, colNumber) => {
          headers[colNumber] = cell.text ? cell.text.trim() : '';
        });
        
        for (let i = 2; i <= worksheet.rowCount; i++) {
          const row = worksheet.getRow(i);
          if (!row.hasValues) continue;
          
          const obj: any = {};
          let hasData = false;
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const header = headers[colNumber];
            if (header) {
              const val = cell.text ? cell.text.trim() : '';
              obj[header] = val;
              if (val) hasData = true;
            }
          });
          if (hasData) {
            rows.push(obj);
          }
        }
      }
      
      if (rows.length === 0) {
        throw new Error('File is empty or invalid.');
      }

      if (this.action === 'add') {
        const allMeds = await this.utilityService.fetchAll<Medication>({
          resourceType: 'Medication',
          checkOrgWideState: false,
          updateOrgWideState: false,
          queries: { status: 'active' }
        }).toPromise() || [];

        this.existingMedsByCode.clear();
        allMeds.forEach(m => {
          const code = m.code?.coding?.[0]?.code?.trim()?.toLowerCase();
          const text = m.code?.text?.trim()?.toLowerCase();
          const keys = [code, text].filter(Boolean);
          keys.forEach(k => {
            if (k) {
              if (!this.existingMedsByCode.has(k)) this.existingMedsByCode.set(k, []);
              this.existingMedsByCode.get(k)!.push(m);
            }
          });
        });
      }

      const total = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        try {
          await this.processRow(row).toPromise();
          this.importResults.success.push(row);
        } catch (error) {
          console.error('Row processing failed', row, error);
          row['_error'] = error;
          this.importResults.failure.push(row);
        }
        this.progress = Math.round(((i + 1) / total) * 100);
      }

      this.snackBar.openFromComponent(SuccessMessageComponent, {
        data: { message: `Import completed: ${this.importResults.success.length} success, ${this.importResults.failure.length} failed.` },
        duration: 5000
      });

      if (this.importResults.success.length > 0) {
        this.resourceRefresh.notify('medication-inventory', { full: true });
      }

    } catch (error: any) {
      this.snackBar.open(`Import failed: ${error.message}`, 'Close', { duration: 5000 });
    } finally {
      this.isImporting = false;
    }
  }

  private parseCSV(text: string): any[] {
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim());
    const dataRows = lines.slice(1);

    return dataRows.map(line => {
      const values = this.parseCSVLine(line);
      const obj: any = {};
      headers.forEach((header, index) => {
        obj[header] = values[index]?.trim() || '';
      });
      return obj;
    });
  }

  private parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  }

  private buildSmartCodeableConcept(rawText: string, code?: string, display?: string, system?: string) {
    if (code || display || system) {
      return {
        text: rawText || display || code,
        coding: [{
          code: code || undefined,
          display: display || undefined,
          system: system || undefined
        }]
      };
    }
    return this.stateService.buildCodeableConcept(rawText);
  }

  private processRow(row: any) {
    if (this.action === 'delete') {
      if (!row.id) throw new Error('ID is required for delete action');
      return this.http.get(`${this.baseApiUrl}/Medication/${row.id}`).pipe(
        switchMap((med: any) => {
          const changesExts = (med.extension || []).filter((e: any) => e.url?.endsWith('/totalRemainingChanges'));
          if (changesExts.length > 0) {
            return throwError(() => new Error('Cannot delete: stock movements have been recorded. Please use Edit to change the stock to 0 instead.'));
          }
          med.status = 'entered-in-error';
          return this.http.put(`${this.baseApiUrl}/Medication/${row.id}`, med);
        })
      );
    }

    // Pre-parsing Validation
    const nameText = row.name_text || row.name;
    if (!nameText) {
      throw new Error('Missing required column: name (or name_text)');
    }

    if (row.sellingPrice === undefined || row.sellingPrice === null || String(row.sellingPrice).trim() === '') {
      throw new Error('Missing required column: sellingPrice');
    }
    if (isNaN(parseFloat(row.sellingPrice))) {
      throw new Error('Invalid sellingPrice: must be a valid number');
    }

    if (row.costPrice === undefined || row.costPrice === null || String(row.costPrice).trim() === '') {
      throw new Error('Missing required column: costPrice');
    }
    if (isNaN(parseFloat(row.costPrice))) {
      throw new Error('Invalid costPrice: must be a valid number');
    }

    if (row.packageType === undefined || row.packageType === null || String(row.packageType).trim() === '') {
      throw new Error('Missing required column: packageType');
    }
    if (row.dispensableUnits === undefined || row.dispensableUnits === null || String(row.dispensableUnits).trim() === '') {
      throw new Error('Missing required column: dispensableUnits');
    }
    if (row.totalRemaining === undefined || row.totalRemaining === null || String(row.totalRemaining).trim() === '') {
      throw new Error('Missing required column: totalRemaining');
    }
    if (isNaN(parseFloat(row.totalRemaining))) {
      throw new Error('Invalid totalRemaining: must be a valid number');
    }

    const medicationResource = this.buildMedicationFromRow(row);
    const chargeItemResource = this.buildChargeItemFromRow(row, medicationResource);

    if (this.action === 'add') {
      if (row.id) {
         throw new Error(`Cannot add: This row already has an ID (${row.id}). If you want to update existing medications, please use 'Edit'.`);
      }

      const code = medicationResource.code?.coding?.[0]?.code?.trim()?.toLowerCase();
      const text = medicationResource.code?.text?.trim()?.toLowerCase();
      const keys = [code, text].filter(Boolean);
      
      let duplicateFound = false;
      for (const k of keys) {
        if (k && this.existingMedsByCode.has(k)) {
          duplicateFound = true;
          if (this.duplicateStrategy === 'skip') {
            return throwError(() => new Error(`Skipped: Duplicate product code found.`));
          }
          break;
        }
      }

      // If we proceed (either no duplicate, or strategy is add_new), add to local dictionary to catch internal duplicates
      keys.forEach(k => {
        if (k) {
          if (!this.existingMedsByCode.has(k)) this.existingMedsByCode.set(k, []);
          this.existingMedsByCode.get(k)!.push(medicationResource);
        }
      });

      return this.http.post(`${this.baseApiUrl}/Medication`, medicationResource).pipe(
        switchMap((medResp: any) => {
          this.stateService.persistOrgWideResource(medResp, 'saved');
          const medicationReference = {
            reference: `Medication/${medResp.id}`,
            display: (medResp.code?.text || 'Unnamed Medication') + (medResp.batch?.lotNumber ? ` [Batch: ${medResp.batch.lotNumber}]` : '')
          };
          const chargeItemToPost = {
            ...chargeItemResource,
            instance: [medicationReference]
          };
          return this.http.post(`${this.baseApiUrl}/ChargeItemDefinition`, chargeItemToPost);
        }),
        catchError(err => { throw err; })
      );
    } else {
      // Edit — clone-and-overlay pattern: start from the server's existing resource
      // (which has meta.tag, meta.security, totalRemainingChanges, etc.) and only
      // overwrite the fields the user explicitly provided in the spreadsheet row.
      if (!row.id) throw new Error('ID is required for edit action');

      return this.http.get(`${this.baseApiUrl}/Medication/${row.id}`).pipe(
        switchMap((original: any) => {
          if (medicationResource.status === 'entered-in-error' && original.status !== 'entered-in-error') {
            const changesExts = (original.extension || []).filter((e: any) => e.url?.endsWith('/totalRemainingChanges'));
            if (changesExts.length > 0) {
              return throwError(() => new Error('Cannot change status to entered-in-error: stock movements have been recorded.'));
            }
          }

          // Deep-clone the original to preserve meta.tag, meta.security, server extensions
          const merged: any = JSON.parse(JSON.stringify(original));

          // Overlay only the TRULY editable fields for existing rows.
          // Note: code, form, manufacturer, amount, and ingredient are NOT editable in the Excel export,
          // so we intentionally DO NOT merge them to avoid destroying server-side codeable concept structures.
          
          if (medicationResource.status && merged.status !== medicationResource.status) {
            merged.status = medicationResource.status;
            // If entered-in-error, zero out totalRemaining
            if (merged.status === 'entered-in-error') {
               merged.extension = merged.extension || [];
               const trIdx = merged.extension.findIndex((e: any) => e.url?.endsWith('/totalRemaining'));
               if (trIdx !== -1 && merged.extension[trIdx].valueQuantity) {
                   merged.extension[trIdx].valueQuantity.value = 0;
               } else if (trIdx === -1) {
                   merged.extension.push({ url: `${this.terminologyServer}/StructureDefinition/totalRemaining`, valueQuantity: { value: 0 } });
               }
            }
          }

          if (medicationResource.batch?.lotNumber || medicationResource.batch?.expirationDate) {
            merged.batch = { ...(merged.batch || {}), ...medicationResource.batch };
          }

          // Overlay amount (package strength) if amountNumeratorValue column is present in the imported row
          if ('amountNumeratorValue' in row) {
            if (medicationResource.amount) {
              merged.amount = medicationResource.amount;
            } else if (merged.amount) {
              // User left blank, cleared, or previously had dummy 0 amount: clear it so it becomes Unspecified
              delete merged.amount;
            }
          }

          // Merge user-provided extensions (inventory fields) into the original's extensions,
          // replacing by URL so we don't duplicate, but keeping server-managed ones intact
          const userExtensions: any[] = medicationResource.extension || [];
          if (userExtensions.length) {
            const existingExts: any[] = Array.isArray(merged.extension) ? merged.extension : [];
            const userExtUrls = new Set();
            
            // Check for totalRemaining changes to record history
            const newTotalRemainingExt = userExtensions.find(e => e.url?.endsWith('/totalRemaining'));
            if (newTotalRemainingExt && merged.status !== 'entered-in-error') {
               const oldTotalRemainingExt = existingExts.find(e => e.url?.endsWith('/totalRemaining'));
               const oldVal = oldTotalRemainingExt?.valueQuantity?.value ?? 0;
               const newVal = newTotalRemainingExt.valueQuantity?.value ?? 0;
               if (oldVal !== newVal) {
                  const userId = this.auth.user?.getValue()?.['fhirResourceId'];
                  const pracRef = userId ? `Practitioner/${userId}` : undefined;
                  const pracChangeExt = pracRef ? { url: 'practitioner', valueReference: { reference: pracRef } } : null;
                  
                  existingExts.push({
                      url: `${this.terminologyServer}/StructureDefinition/totalRemainingChanges`,
                      extension: [
                          { url: 'dateChanged', valueDateTime: new Date().toISOString() },
                          pracChangeExt,
                          { url: 'totalRemainingBeforeChange', valueQuantity: { value: oldVal, unit: oldTotalRemainingExt?.valueQuantity?.unit || newTotalRemainingExt.valueQuantity?.unit } },
                          { url: 'totalRemainingAfterChange', valueQuantity: { value: newVal, unit: newTotalRemainingExt.valueQuantity?.unit } }
                      ].filter(Boolean)
                  });
               }
            }

            for (const userExt of userExtensions) {
               userExtUrls.add(userExt.url);
            }

            merged.extension = [
              ...existingExts.filter((e: any) => !userExtUrls.has(e.url)),
              ...userExtensions
            ];
          }

          return this.http.put(`${this.baseApiUrl}/Medication/${row.id}`, merged).pipe(
            switchMap((medResp: any) => {
              this.stateService.persistOrgWideResource(medResp, 'saved');
              const medicationReference = { 
                reference: `Medication/${row.id}`, 
                display: (medResp.code?.text || 'Unnamed Medication') + (medResp.batch?.lotNumber ? ` [Batch: ${medResp.batch.lotNumber}]` : '') 
              };

              const existingCid = this.chargeItemMap.get(row.id);

              if (existingCid && existingCid.id) {
                // Clone-and-overlay the existing ChargeItemDefinition too
                const mergedCid: any = JSON.parse(JSON.stringify(existingCid));
                mergedCid.instance = [medicationReference];
                // Overlay price components if provided
                if (chargeItemResource.propertyGroup?.[0]?.priceComponent?.length) {
                  mergedCid.propertyGroup = chargeItemResource.propertyGroup;
                }
                // Merge cost-price extension
                const costExtensions: any[] = chargeItemResource.extension || [];
                if (costExtensions.length) {
                  const existCidExts: any[] = Array.isArray(mergedCid.extension) ? mergedCid.extension : [];
                  const costExtUrls = new Set(costExtensions.map((e: any) => e.url));
                  mergedCid.extension = [
                    ...existCidExts.filter((e: any) => !costExtUrls.has(e.url)),
                    ...costExtensions
                  ];
                }
                return this.http.put(`${this.baseApiUrl}/ChargeItemDefinition/${existingCid.id}`, mergedCid);
              } else {
                const chargeItemToPost = { ...chargeItemResource, instance: [medicationReference] };
                return this.http.post(`${this.baseApiUrl}/ChargeItemDefinition`, chargeItemToPost);
              }
            })
          );
        })
      );
    }
  }

  private buildMedicationFromRow(row: any): any {
    const ingredients = [];
    
    // Dynamically find all ingredient indices present in the row headers
    const ingredientIndices = new Set<number>();
    for (const key of Object.keys(row)) {
      const match = key.match(/^ingredient(\d+)_/);
      if (match) {
        ingredientIndices.add(parseInt(match[1], 10));
      }
    }
    
    const sortedIndices = Array.from(ingredientIndices).sort((a, b) => a - b);
    
    for (const i of sortedIndices) {
      const nameText = row[`ingredient${i}_name_text`] || row[`ingredient${i}_name`];
      // Only process if there's a name value provided in this specific row
      if (nameText) {
        ingredients.push({
          itemCodeableConcept: this.buildSmartCodeableConcept(
            nameText,
            row[`ingredient${i}_name_code`],
            row[`ingredient${i}_name_display`],
            row[`ingredient${i}_name_system`]
          ),
          isActive: row[`ingredient${i}_isActive`]?.toLowerCase() === 'true',
          ...(row[`ingredient${i}_strengthNumeratorValue`] !== undefined && 
              row[`ingredient${i}_strengthNumeratorValue`] !== null && 
              String(row[`ingredient${i}_strengthNumeratorValue`]).trim() !== '' && 
              parseFloat(row[`ingredient${i}_strengthNumeratorValue`]) > 0 ? {
            strength: {
              numerator: { 
                value: parseFloat(row[`ingredient${i}_strengthNumeratorValue`]), 
                unit: row[`ingredient${i}_strengthNumeratorUnit`]?.trim() || undefined 
              },
              denominator: { 
                value: parseFloat(row[`ingredient${i}_strengthDenominatorValue`]) || 1, 
                unit: row[`ingredient${i}_strengthDenominatorUnit`]?.trim() || undefined 
              }
            }
          } : {})
        });
      }
    }

    const rawAmtNum = row.amountNumeratorValue;
    const hasAmount = rawAmtNum !== undefined && 
                      rawAmtNum !== null && 
                      String(rawAmtNum).trim() !== '' && 
                      !isNaN(parseFloat(rawAmtNum)) && 
                      parseFloat(rawAmtNum) > 0;

    const medication: any = {
      resourceType: 'Medication',
      status: row.status || 'active',
      code: this.buildSmartCodeableConcept(row.name_text || row.name, row.name_code, row.name_display, row.name_system),
      form: this.buildSmartCodeableConcept(row.form_text || row.form, row.form_code, row.form_display, row.form_system),
      manufacturer: row.manufacturer ? { display: row.manufacturer.trim() } : undefined,
      ...(hasAmount ? {
        amount: {
          numerator: { 
            value: parseFloat(rawAmtNum), 
            unit: row.amountNumeratorUnit?.trim() || undefined 
          },
          denominator: { 
            value: parseFloat(row.amountDenominatorValue) || 1, 
            unit: row.amountDenominatorUnit?.trim() || undefined 
          }
        }
      } : {}),
      ingredient: ingredients.length > 0 ? ingredients : undefined,
      batch: {
        lotNumber: row.lotNumber,
        expirationDate: row.expirationDate ? new Date(row.expirationDate).toISOString() : undefined
      },
      extension: []
    };

    // Inventory Extensions
    const extUrl = this.terminologyServer + '/StructureDefinition/';
    
    if (row.packageType) {
      medication.extension.push({
        url: extUrl + 'packageType',
        valueCodeableConcept: { coding: [{ display: row.packageType }], text: row.packageType }
      });
    }
    
    if (row.dispensableUnits) {
      medication.extension.push({
        url: extUrl + 'dispensableUnits',
        valueString: row.dispensableUnits
      });
    }

    if (row.totalRemaining) {
      medication.extension.push({
        url: extUrl + 'totalRemaining',
        valueQuantity: { value: parseFloat(row.totalRemaining), unit: row.dispensableUnits || row.amountDenominatorUnit }
      });
    }
    if (row.lowStockThreshold) {
      medication.extension.push({
        url: extUrl + 'lowStockThreshold',
        valueQuantity: { value: parseFloat(row.lowStockThreshold), unit: row.dispensableUnits || row.amountDenominatorUnit }
      });
    }
    if (row.lowStockAlertsEnabled) {
      medication.extension.push({
        url: extUrl + 'lowStockAlertsEnabled',
        valueBoolean: row.lowStockAlertsEnabled?.toLowerCase() === 'true'
      });
    }

    return medication;
  }

  private buildChargeItemFromRow(row: any, medResource: any): any {
    const currency = row.currency || 'NGN';
    const components = [];

    if (row.sellingPrice) {
      components.push({
        type: 'base',
        amount: { value: parseFloat(row.sellingPrice), currency }
      });
    }

    const extension = [];
    if (row.costPrice) {
      extension.push({
        url: "http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost",
        extension: [
          { url: "type", valueCode: 'base' },
          { url: "code", valueCodeableConcept: { text: 'cost price component' } },
          { url: "amount", valueMoney: { value: parseFloat(row.costPrice), currency } }
        ]
      });
    }

    const nameText = row.name_text || row.name;

    return {
      resourceType: 'ChargeItemDefinition',
      status: 'active',
      title: (nameText || 'Unnamed Medication') + ' Price Definition',
      url: `https://elikita-server.daalitech.com/ChargeItemDefinition/medication-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      propertyGroup: [{ priceComponent: components }],
      extension: extension
    };
  }

  async downloadFailedRows() {
    if (this.importResults.failure.length === 0) return;

    try {
      const ExcelJS = await import('exceljs');
      const FileSaver = await import('file-saver');
      const Workbook = ExcelJS.Workbook || (ExcelJS as any).default?.Workbook || (ExcelJS as any).default;
      const workbook = new Workbook();
      const sheet = workbook.addWorksheet('Failed Medications');
      
      const headers = [...this.csvHeaders, '_error'];
      sheet.addRow(headers);
      sheet.getRow(1).font = { bold: true };
      
      this.importResults.failure.forEach(row => {
        sheet.addRow(headers.map(h => {
          let val = row[h];
          if (val === null || val === undefined) return '';
          
          if (val instanceof Error || (typeof val === 'object' && 'message' in val)) {
            return (val as any).message || 'Unknown error';
          } else if (typeof val === 'object') {
            return JSON.stringify(val);
          }
          return val;
        }));
      });
      
      sheet.columns.forEach((column: any, index: number) => {
        const headerName = headers[index];
        if (headerName && (headerName.endsWith('_code') || headerName.endsWith('_system') || headerName.endsWith('_display'))) {
          column.hidden = true;
        } else if (headerName === '_error') {
          column.width = 40;
        } else {
          column.width = 15;
        }
      });
      
      const buffer = await workbook.xlsx.writeBuffer();
      const saveAs = FileSaver.saveAs || (FileSaver as any).default?.saveAs || (FileSaver as any).default;
      saveAs(new Blob([buffer]), `medication_import_failed_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (e) {
      console.error('Failed to generate failed rows Excel file', e);
      this.snackBar.open('Failed to generate failed rows Excel file.', 'Close', { duration: 3000 });
    }
  }

  close() {
    this.dialogRef.close();
  }
}
